import * as React from 'react';
import classNames from 'classnames';
import warningSvg from '../../images/warning.svg';
import informationSvg from '../../images/information.svg';
import crossGreySvg from '../../images/crossGrey.svg';
import successSvg from '../../images/success.svg';
import errorSvg from '../../images/error.svg';
import { hasValue, scrollToRef } from '../../lib/utils';

// This is already using DataCapture page styling.
const MessageBox = (props: Props) => {
	let type: string;
	let message: React.ReactNode = null;
	let iconContent;

	const successMessageHasValue = hasValue(props.successMessage);
	const warningHasValue = hasValue(props.warning);
	const lightWarningHasValue = hasValue(props.lightWarning);
	const informationHasValue = hasValue(props.information);
	const errorHasValue = hasValue(props.error);
	const messageBoxRef = React.useRef(null);

	if (
		(successMessageHasValue && (warningHasValue || lightWarningHasValue || errorHasValue)) ||
		(warningHasValue && (lightWarningHasValue || errorHasValue)) ||
		(lightWarningHasValue && errorHasValue)
	) {
		throw new Error('Too many messages. The MessageBox component must only be called with one message at a time.');
	} else if (successMessageHasValue) {
		type = 'success';
		message = props.successMessage;
		iconContent = <img src={successSvg} alt="Success Indication Icon" />;
	} else if (warningHasValue) {
		type = 'warning';
		message = props.warning;
		iconContent = <img src={warningSvg} alt="Warning Icon" />;
	} else if (lightWarningHasValue) {
		type = 'lightWarning';
		message = props.lightWarning;
		iconContent = <img src={warningSvg} alt="Warning Icon" />;
	} else if (informationHasValue) {
		type = 'information';
		message = props.information;
		iconContent = <img src={informationSvg} alt="Warning Icon" />;
	} else if (errorHasValue) {
		type = 'error';
		message = props.error;
		iconContent = <img src={errorSvg} alt="Red Circled Exclamation Mark Icon" />;
	}

	const hasMessage = hasValue(message);

	// Scrolling used to be triggered from the icon's onLoad, which never fires once the browser has cached the
	// image, leaving the message off-screen. Scrolling from an effect instead makes it reliable.
	// `message` is a ReactNode, so depending on it directly would re-scroll on every render whenever a caller
	// passes JSX. Keying on the rendered text instead still moves to a message that has been replaced by a
	// different one, without re-scrolling when only the element identity changed.
	const messageKey = typeof message === 'string' || typeof message === 'number' ? String(message) : type;

	React.useEffect(() => {
		if (hasMessage && messageBoxRef.current) scrollToRef(messageBoxRef);
	}, [hasMessage, messageKey]);

	if (hasValue(message)) {
		return (
			<div
				ref={messageBoxRef}
				className={classNames('dataCapturePageMessage', 'genericPageMessage', 'messageBox', type)}
			>
				{iconContent}
				<p className={classNames(type)}>{message}</p>
				{props.onClose && (
					<button type="button" className="closeButton" onClick={props.onClose}>
						<img src={crossGreySvg} alt="close button" />
					</button>
				)}
			</div>
		);
	}
	return null;
};

interface Props {
	successMessage?: React.ReactNode;
	error?: React.ReactNode;
	warning?: React.ReactNode;
	lightWarning?: React.ReactNode;
	information?: React.ReactNode;
	onClose?: () => void;
}

MessageBox.defaultProps = {
	successMessage: null,
	error: null,
	warning: null,
	lightWarning: null,
	information: null,
	onClose: null
};

export default MessageBox;
